/**
 * The Desktop's style — Windows or Mac — a per-person, per-browser
 * preference, saved like the workspace's view (`fias.vault.view`): in
 * localStorage, read and written defensively (storage can be missing or throw
 * in private windows), and shared live between every component showing it so
 * the Settings switch takes effect at once.
 *
 * Windows is the default; a person on a Mac can switch.
 */
import { useSyncExternalStore } from 'react';
import type { DesktopStyle } from './folder-view';

export const DESKTOP_STYLE_STORAGE_KEY = 'fias.vault.desktopStyle';

export function isDesktopStyle(value: unknown): value is DesktopStyle {
  return value === 'windows' || value === 'mac';
}

function read(): DesktopStyle {
  try {
    const saved = window.localStorage.getItem(DESKTOP_STYLE_STORAGE_KEY);
    return isDesktopStyle(saved) ? saved : 'windows';
  } catch {
    return 'windows';
  }
}

let current: DesktopStyle | null = null;
const listeners = new Set<() => void>();

function snapshot(): DesktopStyle {
  if (current === null) current = read();
  return current;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setDesktopStyle(style: DesktopStyle): void {
  current = style;
  try {
    window.localStorage.setItem(DESKTOP_STYLE_STORAGE_KEY, style);
  } catch {
    // A per-browser convenience only — the switch still applies this visit.
  }
  for (const listener of listeners) listener();
}

/** Tests: forget the cached value so the next read goes to storage. */
export function resetDesktopStyleForTests(): void {
  current = null;
}

export function useDesktopStyle(): [DesktopStyle, (style: DesktopStyle) => void] {
  const style = useSyncExternalStore(subscribe, snapshot, () => 'windows' as const);
  return [style, setDesktopStyle];
}
